import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { User } from '@models/user';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ResellerHistoryService } from '@services/reseller-history.service';
import { SmsHistoryService } from '@services/sms-history.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { formateDateTimeWithSeconds } from '@utils/utils';

@Component({
  selector: 'wsb-history-detail-modal',
  templateUrl: './history-detail-modal.component.html',
  styleUrls: ['./history-detail-modal.component.scss'],
  standalone: false
})
export class HistoryDetailModalComponent implements OnInit {
  @ViewChild('modalExportSmsDetails') modalExportSmsDetails;
  @ViewChild('modalSuccessExport') modalSuccessExport;

  modalExportSmsDetailsModal: NgbModalRef;
  modalSuccessExportModal: NgbModalRef;

  @Input() user: User;
  @Input() hideImport = false;
  @Input() idMessage: string;
  @Input() idSubaccount: number;
  @Input() isRichsms: boolean;
  @Input() hidden = false;
  @Input() scheduled = false;
  @Input() scheduleType: string;

  fc: UntypedFormControl = new UntypedFormControl('XLS', Validators.required);

  gridApi;
  gridColumnApi;
  tableLoading: boolean;
  tableHistoryGridOptions = null;
  tableColumnsDefs: any = [];
  isRichSmsColumnsDefs: any = [];
  paginationPageSize = 10;
  tableHistoryData;
  components = {
    htmlRenderer: AtlasGridHtmlRendererComponent
  };

  creationDate;

  smsDetails;
  smsList;
  creditType;
  lowCreditType = false;

  constructor(
    public activeModal: NgbActiveModal,
    private resellerHistoryService: ResellerHistoryService,
    private smsHistoryService: SmsHistoryService,
    private modalService: NgbModal,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.defineTable();
    this.gridOptionDefinition();

    this.getSmsDetails();
  }

  private getSmsDetails() {
    if (this.idSubaccount) {
      const resellerBody = {
        idSubaccount: this.idSubaccount,
        idMessage: this.idMessage,
        isRichsms: this.isRichsms ? this.isRichsms : false
      };
      return this.resellerHistoryService.getSmsSentDetails(resellerBody).subscribe(details => {
        if (details.data.length > 0) {
          this.smsDetails = details.data[0];
          this.creationDate = formateDateTimeWithSeconds((this.smsDetails?.data)[0].createTime);
          this.creditType = details.data[0].idMessageType.toLowerCase();
        }
      });
    }
    if (this.scheduled) {
      this.smsHistoryService.getSmsScheduledDetails(this.idMessage, this.scheduleType).subscribe(details => {
        this.creditType = details.idMessageType.toLowerCase();
        this.smsDetails = details;
        this.creationDate = formateDateTimeWithSeconds(this.smsDetails?.createTime);
      });
      return;
    }

    const sentBody = {
      idMessage: this.idMessage,
      isRichsms: this.isRichsms ? this.isRichsms : false
    };

    this.smsHistoryService.getSingleSmsSentDetails(sentBody).subscribe(details => {
      this.smsDetails = details;
      this.creditType = details.idMessageType.toLowerCase();
      this.creationDate = formateDateTimeWithSeconds(this.smsDetails.createTime);
      this.lowCreditType = details.idMessageType === 'SI' || details.idMessageType === 'TI' ? true : false;
    });
  }

  defineTable() {
    this.tableColumnsDefs = [
      {
        headerNameTranslate: 'common_number_recipient',
        field: 'rcptNumber',
        sortable: false
      },
      {
        headerNameTranslate: 'common_name_and_surname',
        field: 'contactsData',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (!params || !params.data || !params.data.contact) {
              return '';
            }

            return this.contactInfo(params.data.contact);
          }
        },
        sortable: false
      },
      {
        headerNameTranslate: 'common_data_added',
        field: 'dateForSend',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const dateTime = formateDateTimeWithSeconds(params.data.dateForSend).split(' ');
              const date = dateTime[0];
              const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
              return '<div class="dateTimeDetail"><span>' + date + '</span> <span>' + time + '</span></div>';
            }
            return '';
          }
        }
      },
      {
        headerNameTranslate: 'common_data_sent',
        field: 'sendDate',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              const dateTime = formateDateTimeWithSeconds(params.data.sendDate).split(' ');
              const date = dateTime[0];
              const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
              return '<div class="dateTimeDetail"><span>' + date + '</span> <span>' + time + '</span></div>';
            }
            return '';
          }
        }
      },
      {
        headerNameTranslate: 'common_status',
        field: 'messageStatus',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              return this.translateService.instant('sms_status_' + params.data.status);
            } else {
              return '';
            }
          }
        }
      }
    ];

    if (this.isRichsms) {
      this.isRichSmsColumnsDefs = [
        {
          headerNameTranslate: 'common_total_clicks',
          field: 'totalClicks',
          sortable: false
        },
        {
          headerNameTranslate: 'common_first_click_date',
          field: 'firstClickDate',
          sortable: false,
          cellRenderer: 'htmlRenderer',
          cellRendererParams: {
            content: params => {
              if (params && params.data && params.data.firstClickDate) {
                const dateTime = formateDateTimeWithSeconds(params.data.firstClickDate).split(' ');
                const date = dateTime[0];
                const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
                return '<div class="dateTimeDetail"><span>' + date + '</span> <span>' + time + '</span></div>';
              }
              return '';
            }
          }
        },
        {
          headerNameTranslate: 'sms_detail_history_rich_click',
          field: 'lastClickDate',
          sortable: false,
          cellRenderer: 'htmlRenderer',
          cellRendererParams: {
            content: params => {
              if (params && params.data && params.data.lastClickDate) {
                const dateTime = formateDateTimeWithSeconds(params.data.lastClickDate).split(' ');
                const date = dateTime[0];
                const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
                return '<div class="dateTimeDetail"><span>' + date + '</span> <span>' + time + '</span></div>';
              }
              return '';
            }
          }
        }
      ];

      this.tableColumnsDefs = [...this.tableColumnsDefs, ...this.isRichSmsColumnsDefs];
    }
  }

  contactInfo(contact) {
    let labels = ['common_name', 'common_surname'];

    this.translateService.get(labels).subscribe(translatedLabels => {
      labels = [translatedLabels[labels[0]], translatedLabels[labels[1]]];
    });

    let name = '';
    let surname = '';

    if (contact.name) {
      name = '<span>' + labels[0] + ': ' + (contact?.name ? contact?.name : '') + '</span>';
    }

    if (contact.surname) {
      surname = '<span>' + labels[1] + ': ' + (contact?.surname ? contact?.surname : '') + '</span>';
    }

    return '<div class="dateTimeDetail">' + name + surname + '</div>';
  }

  gridOptionDefinition() {
    const that = this;

    this.tableHistoryGridOptions = AtlasGridOptions.deserialize({
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadList.bind(that)
    });
  }

  reloadList(startRow?, endRow?, sort?) {
    if (this.idSubaccount) {
      const resellerBody = {
        idSubaccount: this.idSubaccount,
        idMessage: this.idMessage,
        isRichsms: this.isRichsms ? this.isRichsms : false
      };
      return this.resellerHistoryService.getSmsSentDetails(
        resellerBody,
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        sort && sort.length ? sort[0].colId : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null
      );
    }

    const sentBody = {
      idMessage: this.idMessage,
      isRichsms: this.isRichsms ? this.isRichsms : false
    };
    return this.smsHistoryService.getSmsSentDetails(
      sentBody,
      1 + startRow / (endRow - startRow),
      endRow - startRow,
      sort && sort.length ? sort[0].colId : null,
      sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null
    );
  }

  export() {
    if (this.idSubaccount) {
      return this.resellerHistoryService.exportSendSmsDetail(this.fc.value, this.idMessage, this.idSubaccount).subscribe(() => {
        this.modalService.dismissAll();
        this.openModalSuccessExport();
      });
    }

    this.smsHistoryService.exportSendSmsDetail(this.fc.value, this.idMessage, this.user.profile.email).subscribe(() => {
      this.modalService.dismissAll();
      this.openModalSuccessExport();
    });
  }

  openExportModal(): void {
    this.modalExportSmsDetailsModal = this.modalService.open(this.modalExportSmsDetails);
    this.modalExportSmsDetailsModal.result.then(
      result => {},
      reason => {}
    );
  }

  openModalSuccessExport(): void {
    this.modalSuccessExportModal = this.modalService.open(this.modalSuccessExport);
    this.modalSuccessExportModal.result.then(
      result => {
        this.modalService.dismissAll();
      },
      reason => {
        this.modalService.dismissAll();
      }
    );
  }

  getDeliveredCount() {
    let countDelivered = 0;
    if (this.smsDetails?.statusCount?.delivered) {
      countDelivered += this.smsDetails.statusCount.delivered.sms;
    }
    if (this.smsDetails?.statusCount?.sent) {
      countDelivered += this.smsDetails.statusCount.sent.sms;
    }
    return countDelivered;
  }
}
